// Un producto del catálogo: foto o ícono, nombre, presentación, precio por bulto, stock y el control para pedirlo. En
// el celular es una fila de la lista; en la grilla de la tablet y la computadora (contenedor de 36rem o más), una
// tarjeta. El stock va en gris con su ícono: el verde de "hay" chocaba con marcas de cualquier color; solo "quedan
// pocos" (ámbar) y "sin stock" (rojo) llevan color.
import { formatoGs } from '../compartido';
import { cx, Icono } from '@reparto/ui';
import { bultosDe, fijarBultos } from '../datos/carrito';
import { cambiarCarrito, useCarrito } from '../datos/estado-carrito';
import { cantidadEnUnidad, nombreUnidad, presentacion } from '../datos/formato';
import type { Articulo, Frecuente } from '../datos/tipos';
import { ControlCantidad } from './Cantidad';
import { Miniatura } from './Miniatura';

/** Hasta cuánto se puede pedir: lo que hay, si la empresa no deja pedir sin stock y muestra la cantidad. */
export const topeDe = (a: Pick<Articulo, 'stock_bultos' | 'se_puede_pedir'>, permitirSinStock: boolean) =>
  !permitirSinStock && a.stock_bultos !== null && a.stock_bultos > 0 ? a.stock_bultos : null;

export function EstadoStock({ a }: { a: Pick<Articulo, 'disponible' | 'stock_bultos' | 'unidad'> }) {
  let texto: string;
  let icono = 'inventory_2';
  let tono = 'text-stone-600';
  if (a.disponible === false) {
    texto = 'Sin stock';
    icono = 'block';
    tono = 'font-medium text-rose-700';
  } else if (a.stock_bultos !== null) {
    const pocos = a.stock_bultos <= 5;
    texto = `${pocos ? 'Quedan' : 'Hay'} ${cantidadEnUnidad(a.stock_bultos, a.unidad)}`;
    if (pocos) tono = 'font-medium text-amber-800';
  } else if (a.disponible === true) texto = 'Hay stock';
  else return null;
  return (
    <span className={cx('inline-flex items-center gap-1 text-[15px]', tono)}>
      <Icono nombre={icono} tamano={18} />
      {texto}
    </span>
  );
}

export function Precio({ precio, unidad }: { precio: number | null; unidad: Articulo['unidad'] | null }) {
  if (precio === null) return null;
  return (
    <span className="leading-tight">
      <span className="block text-lg font-bold tabular-nums text-stone-900">{formatoGs(precio)}</span>
      <span className="block text-sm text-stone-600">por {nombreUnidad(unidad)}</span>
    </span>
  );
}

/** La lista de productos: filas en el celular, grilla de 2 a 4 columnas desde 36rem de ancho. */
export const CLASES_LISTA =
  'divide-y divide-stone-100 rounded-3xl border border-stone-200 bg-white shadow-sm @xl:shadow-none ' +
  '@xl:grid @xl:grid-cols-2 @xl:gap-3 @xl:divide-y-0 @xl:border-0 @xl:bg-transparent @[44rem]:grid-cols-3 @[56rem]:grid-cols-4';

export function FilaProducto({
  a,
  permitirSinStock,
  frecuente,
}: {
  a: Articulo | Frecuente;
  permitirSinStock: boolean;
  /** En "Lo de siempre": entra con la cantidad de la última vez. */
  frecuente?: boolean;
}) {
  const bultos = useCarrito((s) => bultosDe(s.carrito, a.id));
  const sugeridos = frecuente && 'bultos_sugeridos' in a ? a.bultos_sugeridos : 1;
  const tope = topeDe(a, permitirSinStock);
  const cambiar = (n: number) => cambiarCarrito((c) => fijarBultos(c, a, n));
  return (
    <li className="flex gap-3 px-4 py-4 @xl:flex-col @xl:gap-3 @xl:rounded-2xl @xl:border @xl:border-stone-200 @xl:bg-white @xl:p-3">
      <Miniatura
        nombre={a.nombre}
        categoria={a.categoria}
        foto={a.foto_url}
        fotoMini={a.foto_mini_url}
        apagada={!a.se_puede_pedir}
        adaptable
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="line-clamp-2 text-[17px] leading-snug font-semibold text-stone-900">{a.nombre}</h3>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[15px] text-stone-600">
          <span>{presentacion(a.unidad, a.unidades_por_bulto)}</span>
          <EstadoStock a={a} />
        </p>
        {frecuente && 'bultos_sugeridos' in a ? (
          <p className="mt-0.5 text-[15px] text-stone-600">
            La última vez: <b className="font-semibold text-stone-800">{cantidadEnUnidad(sugeridos, a.unidad)}</b>
          </p>
        ) : null}
        <div className="mt-2 flex flex-wrap items-end justify-between gap-2 @xl:mt-auto">
          <Precio precio={a.precio_bulto} unidad={a.unidad} />
          <div className="ml-auto @xl:w-full @xl:*:w-full">
            <ControlCantidad
              valor={bultos}
              onCambiar={cambiar}
              max={tope}
              nombre={a.nombre}
              deshabilitado={!a.se_puede_pedir && bultos === 0}
              textoAgregar={!a.se_puede_pedir ? 'Sin stock' : frecuente ? `Agregar ${sugeridos}` : 'Agregar'}
              alAgregar={sugeridos}
            />
          </div>
        </div>
      </div>
    </li>
  );
}
